import React, { useState, useEffect } from 'react';
import axios from 'axios';

import API_BASE_URL from '../config';
import MatchesList from './MatchesList';

import { useNavigate } from 'react-router-dom';

import {
  Paper,
  Box,
  Typography,
  Button,
  Stack,
  CircularProgress,
  Fade,
  useTheme,
  alpha,
} from '@mui/material';

import {
  WomanRounded,
  ManRounded,
} from '@mui/icons-material';

import { styled } from '@mui/material/styles';

// ======================================================
// Styled Components
// ======================================================

const StyledPaper = styled(Paper)(({ theme }) => ({
  background: `linear-gradient(
    135deg,
    ${theme.palette.background.paper} 0%,
    ${alpha(theme.palette.primary.light, 0.1)} 100%
  )`,
  borderRadius: theme.spacing(3),
  padding: theme.spacing(4),
  position: 'relative',
  overflow: 'hidden',
  boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
}));

const AgeGroupButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'selected',
})(({ theme, selected }) => ({
  borderRadius: theme.spacing(4),
  padding: theme.spacing(1, 3),
  transition: 'all 0.3s ease',
  margin: theme.spacing(0.5),

  backgroundColor: selected
    ? theme.palette.primary.main
    : 'transparent',

  color: selected
    ? theme.palette.primary.contrastText
    : theme.palette.text.primary,

  border: `2px solid ${
    selected
      ? theme.palette.primary.main
      : theme.palette.divider
  }`,

  '&:hover': {
    transform: 'translateY(-2px)',

    backgroundColor: selected
      ? theme.palette.primary.dark
      : alpha(theme.palette.primary.main, 0.1),
  },
}));

const GenderSection = styled(Box)(({ theme }) => ({
  background: alpha(
    theme.palette.background.paper,
    0.6
  ),

  borderRadius: theme.spacing(2),
  padding: theme.spacing(3),
  transition: 'all 0.3s ease',

  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 12px 24px rgba(0,0,0,0.1)',
  },
}));

// ======================================================
// Find Partner
// ======================================================

const FindPartner = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [selectedAgeGroup, setSelectedAgeGroup] =
    useState('');

  const [matches, setMatches] = useState({
    male: [],
    female: [],
  });

  const [loading, setLoading] = useState({
    male: false,
    female: false,
  });

  // ====================================================
  // Age Groups
  // ====================================================

  const ageGroups = [
    {
      label: '18-22',
      min: 18,
      max: 22,
    },
    {
      label: '23-26',
      min: 23,
      max: 26,
    },
    {
      label: '27-31',
      min: 27,
      max: 31,
    },
    {
      label: '32-36',
      min: 32,
      max: 36,
    },
    {
      label: '37-41',
      min: 37,
      max: 41,
    },
    {
      label: '42-80',
      min: 42,
      max: 80,
    },
  ];

  // ====================================================
  // Check Login
  // ====================================================

  useEffect(() => {
    const userUid = localStorage.getItem('uid');

    if (!userUid) {
      navigate('/login', {
        replace: true,
      });
    }
  }, [navigate]);

  // ====================================================
  // Search Matches
  // ====================================================

  const searchMatches = async (
    gender,
    ageGroup
  ) => {
    const genderKey = gender.toLowerCase();

    setLoading((prev) => ({
      ...prev,
      [genderKey]: true,
    }));

    try {
      const response = await axios.get(
        `${API_BASE_URL}/find-match`,
        {
          params: {
            gender:
              gender === 'Male'
                ? 'Male'
                : 'Female',

            minAge: ageGroup.min,
            maxAge: ageGroup.max,
          },

          withCredentials: true,
        }
      );

      setMatches((prev) => ({
        ...prev,

        [genderKey]: Array.isArray(
          response.data
        )
          ? response.data
          : [],
      }));
    } catch (error) {
      console.error(
        `Error fetching ${gender} matches:`,
        error
      );

      if (error.response?.status === 401) {
        navigate('/login', {
          replace: true,
        });
      } else {
        setMatches((prev) => ({
          ...prev,
          [genderKey]: [],
        }));
      }
    } finally {
      setLoading((prev) => ({
        ...prev,
        [genderKey]: false,
      }));
    }
  };

  // ====================================================
  // Age Group Selection
  // ====================================================

  const handleAgeGroupSelect = (group) => {
    setSelectedAgeGroup(group.label);

    // Reset previous results
    setMatches({
      male: [],
      female: [],
    });

    // Search both genders
    searchMatches('Male', group);
    searchMatches('Female', group);
  };

  // ====================================================
  // UI
  // ====================================================

  return (
    <Box>
      <Fade in timeout={1000}>
        <StyledPaper>
          {/* ==========================================
              HEADER
          ========================================== */}

          <Box
            sx={{
              textAlign: 'center',
              mb: 6,
            }}
          >
            <Typography
              variant="h4"
              gutterBottom
              sx={{
                fontWeight: 700,

                background: `linear-gradient(
                  45deg,
                  ${theme.palette.primary.main},
                  ${theme.palette.secondary.main}
                )`,

                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                color: 'transparent',
              }}
            >
              Find Your Perfect Match
            </Typography>

            <Typography
              variant="h6"
              color="text.secondary"
              sx={{
                mb: 4,
              }}
            >
              Discover compatible partners in your
              preferred age group
            </Typography>
          </Box>

          {/* ==========================================
              AGE GROUP
          ========================================== */}

          <GenderSection>
            <Typography
              variant="h6"
              align="center"
              sx={{
                mb: 2,
                fontWeight: 600,
              }}
            >
              Select Age Group
            </Typography>

            <Stack
              direction="row"
              spacing={1}
              justifyContent="center"
              flexWrap="wrap"
              sx={{
                mb: 4,
                rowGap: 1,
              }}
            >
              {ageGroups.map((group) => (
                <AgeGroupButton
                  key={group.label}
                  selected={
                    selectedAgeGroup ===
                    group.label
                  }
                  onClick={() =>
                    handleAgeGroupSelect(group)
                  }
                >
                  {group.label}
                </AgeGroupButton>
              ))}
            </Stack>

            {/* ========================================
                MATCH RESULTS
            ======================================== */}

            <Box
              sx={{
                display: 'grid',

                gridTemplateColumns: {
                  xs: '1fr',
                  md: '1fr 1fr',
                },

                gap: 4,
              }}
            >
              {/* ======================================
                  MEN
              ====================================== */}

              <Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: 1,
                    mb: 3,
                  }}
                >
                  <ManRounded />

                  <Typography variant="h5">
                    Men
                  </Typography>
                </Box>

                {loading.male ? (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      py: 4,
                    }}
                  >
                    <CircularProgress />
                  </Box>
                ) : matches.male.length > 0 ? (
                  <MatchesList
                    matches={matches.male}
                  />
                ) : selectedAgeGroup ? (
                  <Typography
                    align="center"
                    color="text.secondary"
                    sx={{ py: 3 }}
                  >
                    No matches found.
                  </Typography>
                ) : (
                  <Typography
                    align="center"
                    color="text.secondary"
                    sx={{ py: 3 }}
                  >
                    Select an age group to find
                    matches.
                  </Typography>
                )}
              </Box>

              {/* ======================================
                  WOMEN
              ====================================== */}

              <Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: 1,
                    mb: 3,
                  }}
                >
                  <WomanRounded />

                  <Typography variant="h5">
                    Women
                  </Typography>
                </Box>

                {loading.female ? (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      py: 4,
                    }}
                  >
                    <CircularProgress />
                  </Box>
                ) : matches.female.length > 0 ? (
                  <MatchesList
                    matches={matches.female}
                  />
                ) : selectedAgeGroup ? (
                  <Typography
                    align="center"
                    color="text.secondary"
                    sx={{ py: 3 }}
                  >
                    No matches found.
                  </Typography>
                ) : (
                  <Typography
                    align="center"
                    color="text.secondary"
                    sx={{ py: 3 }}
                  >
                    Select an age group to find
                    matches.
                  </Typography>
                )}
              </Box>
            </Box>
          </GenderSection>
        </StyledPaper>
      </Fade>
    </Box>
  );
};

export default FindPartner;